import { defineStore } from 'pinia';
import { IClub } from '@/storePinia/stores/club/interfaces/IClub';
import { useResultsStore } from '@/storePinia/stores/results';
import useLoader from '@use/useLoader/useLoader';
import axios from 'axios';

const loader = useLoader();

type IClubGetters = {
  inChoice(state: IClub): (table: string, value: string, i: string) => boolean | undefined;
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  getItemByPageUrl(state: IClub): IClub['items'][0] | undefined | {};
  getItemByFooterMenuLink(state: IClub): IClub['items'][0] | undefined;
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  getItem(state: IClub): IClub['items'][0] | undefined | {};
};

type IClubActions = {
  setChoice(payload: { table: string; value: string; i: string }): void;
  getDataByStep(payload: number): Promise<void>;
  addClubSubscribe(payload: { [key: string]: string | number | undefined }): Promise<void>;
};

export const useClubStore = defineStore<'club', IClub, IClubGetters, IClubActions>('club', {
  state: (): IClub => ({
    step: 1,
    maxStep: 3,
    complete: false,
    error: '',
    /** done because of 2 different locations of call (1. Auto - by page url and 2. From click) */
    autoOpen: false,
    choice: {},
    items: [],
    routes: {},
  }),
  getters: {
    inChoice: (state) => (table, value, i) => {
      try {
        let values: string[] = [];
        let keys: string[] = [];

        const choice = state.choice?.[table];
        if (choice) {
          keys = Object.keys(choice);
          values = Object.values(choice);
        }
        // eslint-disable-next-line eqeqeq
        return !!values.find((el) => el == value) && !!keys.find((el) => el == i);
      } catch (err) {
        console.log(err);
        return undefined;
      }
    },
    getItemByPageUrl: (state) => {
      try {
        return state.items?.find((el) => el?.isShowOnThisPage) ?? {};
      } catch (err) {
        console.log(err);
        return undefined;
      }
    },
    getItemByFooterMenuLink: (state) => {
      try {
        return state.items?.[0];
      } catch (err) {
        console.log(err);
        return undefined;
      }
    },
    getItem(state) {
      try {
        return state.autoOpen ? this.getItemByPageUrl : this.getItemByFooterMenuLink;
      } catch (err) {
        console.log(err);
        return undefined;
      }
    },
  },
  actions: {
    setChoice(payload) {
      try {
        const { table, value, i } = payload;
        this.choice[table] = {
          [i]: value,
        };
      } catch (err) {
        console.error(err);
      }
    },
    async getDataByStep(payload) {
      try {
        loader.start();

        const resultsStore = useResultsStore();

        const { choice } = this;
        const choiceKeys = Object.keys(choice);
        const choiceValues = Object.values(choice);
        /**
         * if user don't chose picture - show error
         */
        if (choiceKeys.length < payload - 1) {
          this.$patch({
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            error: resultsStore.messages?.sovaClub?.chosePicture,
          });

          loader.stop();
          return;
        }

        /**
         * mechanic of sending request for creating Bitrix highload-block field by user choice
         */
        if (payload > this.maxStep) {
          const item = this.getItem;
          if (item && 'id' in item) {
            const requestData: { [key: string]: string | number | undefined } = {
              UF_IBLOCK_ELEMENT: item.id,
            };
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < choiceKeys.length; i++) {
              const hlValues = Object.values(choiceValues[i]);
              // eslint-disable-next-line no-plusplus
              for (let index = 0; index < hlValues.length; index++) {
                requestData[`UF_TABLE_NAME_${i + 1}`] = choiceKeys[i];
                requestData[`UF_XML_ID_${i + 1}`] = hlValues[index];
              }
            }

            await this.addClubSubscribe(requestData);
            loader.stop();
          }

          return;
        }
        /**
         * standard mechanic of transition to the next step
         */
        const {
          status,
          data: responseData,
        }: {
          status: number;
          data: IClub;
        } = await axios.get(window.location.href, {
          params: {
            ...resultsStore.getRequestLang,
            step: payload,
            apiClub: 'y',
          },
        });
        if (status !== 200) throw new Error(`Bad request response status: ${status}`);

        /**
         * @todo do method of clear errors
         */
        this.$patch({
          ...responseData,
          step: payload,
          ...{
            choice: this.choice,
            maxStep: this.maxStep,
            autoOpen: this.autoOpen,
          },
        });
      } catch (err) {
        console.error(err);
        return;
      } finally {
        loader.stop();
      }
    },
    async addClubSubscribe(payload) {
      try {
        if (!this.routes.addClubSubscribe) return undefined;
        const { path, action } = this.routes.addClubSubscribe;

        const {
          status,
          data: responseData,
        }: {
          status: number;
          data: IClub;
        } = await axios.post(path, {
          ...payload,
          action,
        });

        if (status !== 200) throw new Error(`Bad request response status: ${status}`);

        // commit('club/setData', { ...data, ...responseData });
        this.$patch(responseData);
        return undefined;
      } catch (err) {
        console.error(err);
        return undefined;
      }
    },
  },
});
